/**
 * Sticky Header Logo Management
 * Logo switching between normal and sticky logos for sticky header and dropdown menu states
 */

class StickyHeaderLogo {
  constructor() {
    this.header = document.querySelector('#shopify-section-header .header');
    this.headerSection = document.querySelector('#shopify-section-header');
    this.isSticky = false;
    this.isMenuOpen = false;
    this.isHomepage = false;
    this.forceUseStickyLogo = false;
    this.resizeObserver = null;
    this.init();
  }

  init() {
    if (!this.header) return;
    
    // Initialize header height tracking
    this.initHeaderHeight();
  
    return;
  }

  /**
   * Initialize header height tracking and set CSS variable
   */
  initHeaderHeight() {
    if (!this.headerSection) return;
    
    // Set initial header height
    this.updateHeaderHeight();
    
    // Set up ResizeObserver to track header height changes
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateHeaderHeight();
      });
      
      this.resizeObserver.observe(this.headerSection);
    }
    
    // Also update on window resize as fallback
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateHeaderHeight();
      }, 100);
    });
    
  }

  /**
   * Update header height CSS variable
   */
  updateHeaderHeight() {
    if (!this.headerSection) return;
    
    const headerHeight = this.headerSection.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  }

  findLogoElements() {
    // Find normal and sticky logos (exclude any inverse/transparent logos)
    this.normalLogos = this.header.querySelectorAll('.header__heading-logo:not(.header__heading-logo--sticky):not(.inverse)');
    this.stickyLogos = this.header.querySelectorAll('.header__heading-logo--sticky:not(.inverse)');
  }

  setupStickyObserver() {
    const headerSection = document.querySelector('#shopify-section-header');
    if (!headerSection) return;
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const wasSticky = this.isSticky;
          this.isSticky = headerSection.classList.contains('shopify-section-header-sticky');
          
          if (wasSticky !== this.isSticky) {
            this.updateLogos();
          }
        }
      });
    });

    observer.observe(headerSection, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Initial check
    this.isSticky = headerSection.classList.contains('shopify-section-header-sticky');
    this.updateLogos();
  }

  setupMenuObserver() {
    // Monitor dropdown menu state changes
    const menuObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const wasMenuOpen = this.isMenuOpen;
          
          // Check for various dropdown open classes
          this.isMenuOpen = this.header.classList.contains('dropdown-open') ||
                           this.header.classList.contains('dropdown-menu-open') ||
                           this.header.classList.contains('header--dropdown-active') ||
                           this.header.closest('#shopify-section-header')?.classList.contains('dropdown-active');
          
          if (wasMenuOpen !== this.isMenuOpen) {
            this.updateLogos();
          }
        }
      });
    });

    // Observe header for dropdown classes
    menuObserver.observe(this.header, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Also observe the header section for dropdown-active class
    const headerSection = this.header.closest('#shopify-section-header');
    if (headerSection) {
      menuObserver.observe(headerSection, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Listen for custom dropdown events from header-dropdown.js
    document.addEventListener('headerDropdownChange', (event) => {
      const wasMenuOpen = this.isMenuOpen;
      this.isMenuOpen = event.detail.isOpen;
      
      if (wasMenuOpen !== this.isMenuOpen) {
        this.updateLogos();
      }
    });

    // Monitor dropdown menu elements for open state
    const dropdownMenus = document.querySelectorAll('.list-menu-dropdown, .mega-menu, .dropdown-menu, .submenu');
    dropdownMenus.forEach(menu => {
      const menuMutationObserver = new MutationObserver(() => {
        const anyMenuOpen = Array.from(dropdownMenus).some(dropdown => {
          const computedStyle = window.getComputedStyle(dropdown);
          return computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden';
        });
        
        if (anyMenuOpen !== this.isMenuOpen) {
          this.isMenuOpen = anyMenuOpen;
          this.updateLogos();
        }
      });

      menuMutationObserver.observe(menu, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    });

    // Initial check
    this.isMenuOpen = this.header.classList.contains('dropdown-open') ||
                     this.header.classList.contains('dropdown-menu-open') ||
                     this.header.classList.contains('header--dropdown-active') ||
                     this.header.closest('#shopify-section-header')?.classList.contains('dropdown-active');
  }

  updateLogos() {
    // Update header height when state changes
    this.updateHeaderHeight();
    
    // For non-homepage pages, always use sticky logo
    if (this.forceUseStickyLogo) {
      this.switchLogos(true);
      return;
    }
    
    // For homepage: use sticky logo if either sticky header is active OR dropdown menu is open
    const shouldUseSticky = this.isSticky || this.isMenuOpen;
    this.switchLogos(shouldUseSticky);
  }

  switchLogos(useStickyLogo) {
    if (this.stickyLogos.length === 0) {
      return; // No sticky logos available, keep normal ones
    }

    if (useStickyLogo) {
      // Hide normal logos, show sticky logos
      this.normalLogos.forEach(logo => {
        logo.style.display = 'none';
      });
      
      this.stickyLogos.forEach(logo => {
        logo.style.display = 'block';
      });
    } else {
      // Show normal logos, hide sticky logos
      this.normalLogos.forEach(logo => {
        logo.style.display = '';
      });
      
      this.stickyLogos.forEach(logo => {
        logo.style.display = 'none';
      });
    }
  }

  /**
   * Cleanup method to remove observers
   */
  cleanup() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}

// Store instance globally for cleanup if needed
let stickyHeaderLogoInstance = null;

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  stickyHeaderLogoInstance = new StickyHeaderLogo();
});

// Also initialize on theme section load (for Shopify theme editor)
document.addEventListener('shopify:section:load', () => {
  // Cleanup previous instance if exists
  if (stickyHeaderLogoInstance) {
    stickyHeaderLogoInstance.cleanup();
  }
  stickyHeaderLogoInstance = new StickyHeaderLogo();
});
